import Modal from '@components/ui/Modal';
import { useHealthGuardState } from '@/hooks/useHealthGuardState';
// Domain modules, not the vocabulary barrel: this modal ships with the console
// shell, and the barrel would pull every vocabulary module into the entry chunk.
import {
  healthGuardReasonVocabulary,
  healthGuardStateVocabulary,
} from '@/presentation/vocabulary/healthGuard';
import { technicalCodeText } from '@/presentation/vocabulary/core';
import type { HealthGuardSnapshot } from '@/types/surveillance';

type HealthGuardDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  snapshot: HealthGuardSnapshot | null;
};

export default function HealthGuardDetailsModal({
  isOpen,
  onClose,
  snapshot,
}: HealthGuardDetailsModalProps) {
  const { guardState, guardReason, guardQueuedText, guardCpuText } =
    useHealthGuardState(snapshot);
  const systemHealth =
    typeof snapshot?.system_health === 'string' && snapshot.system_health.trim()
      ? snapshot.system_health
      : null;
  const stateTerm = healthGuardStateVocabulary.describe(guardState);
  const reasonTerm = healthGuardReasonVocabulary.describe(guardReason);
  const technicalLines = [
    technicalCodeText(healthGuardStateVocabulary, stateTerm),
    technicalCodeText(healthGuardReasonVocabulary, reasonTerm),
    systemHealth ? `System health: ${systemHealth}` : null,
  ].filter((line): line is string => line !== null);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="System checks (all devices)" size="md">
      <div className="space-y-4 text-sm text-ink">
        <p>
          SoundOps pauses automatic re-tuning on every device when it is overloaded or its health
          reports are missing. It resumes by itself once things recover.
        </p>
        <div className="rounded-md border border-border-subtle bg-surface-recessed px-3 py-2">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-muted">State</p>
              <p className="font-semibold text-ink-strong">{stateTerm.text}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-muted">Waiting</p>
              <p className="font-semibold text-ink-strong">{guardQueuedText || '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-muted">CPU</p>
              <p className="font-semibold text-ink-strong">{guardCpuText || '—'}</p>
            </div>
          </div>
          {reasonTerm.code ? (
            <p className="mt-2 text-xs text-ink">Reason: {reasonTerm.text}</p>
          ) : null}
        </div>
        {technicalLines.length > 0 ? (
          <details className="text-xs text-ink-muted">
            <summary className="cursor-pointer font-semibold">Technical details</summary>
            <ul className="so-data-text mt-1 space-y-0.5 break-words">
              {technicalLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </details>
        ) : null}
        <p className="text-xs text-ink-muted">
          Open a device to see its live audio, checks and history.
        </p>
      </div>
    </Modal>
  );
}
